/**
 * URL-состояние дашборда
 * ======================
 *
 * Раздел, активная вкладка и все фильтры живут в query-параметрах адресной
 * строки — поэтому ссылку можно скинуть коллеге, и он увидит ровно тот же
 * экран с теми же цифрами.
 *
 * ВАЖНО: если в ссылке есть хотя бы один наш параметр, она считается ПОЛНЫМ
 * описанием экрана. Иначе шаренная ссылка подмешала бы localStorage получателя
 * (его кампании, его проект) и показала бы другие данные под тем же URL.
 * Пустой URL (заход «на сайт») — состояние берётся из localStorage как раньше.
 *
 * Даты пишутся как YYYY-MM-DD по локальным компонентам — без timezone-сдвига,
 * так же как они уходят на сервер (см. FiltersService).
 */

import { normalizeStartDate, normalizeEndDate, formatTimeString, parseTimeString } from './dateUtils';

export const URL_KEYS = {
  section: 'section',
  tab: 'tab',
  startDate: 'from',
  endDate: 'to',
  startTime: 'fromTime',
  endTime: 'toTime',
  project: 'project',
  mediaSource: 'source',
  campaigns: 'campaign', // повторяющийся ключ: ?campaign=A&campaign=B
  platform: 'platform',
  clientVersions: 'cver', // повторяющийся ключ: ?cver=1.10.0&cver=1.9.2
  installVersions: 'iver', // повторяющийся ключ, как cver
  applyPlatformFees: 'net',
  includeOrganic: 'organic'
};

const ALL_KEYS = Object.values(URL_KEYS);

const DEFAULT_START_TIME = { hours: 0, minutes: 0 };
const DEFAULT_END_TIME = { hours: 23, minutes: 59 };

const isBlank = (value) => value === undefined || value === null || value === '';

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export const formatDateParam = (date) => {
  if (!date) return null;
  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseDateParam = (value, edge = 'start') => {
  if (isBlank(value)) return null;
  const match = DATE_PATTERN.exec(value);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return null;
  return edge === 'end' ? normalizeEndDate(date) : normalizeStartDate(date);
};

const sameTime = (time, reference) =>
  !!time && time.hours === reference.hours && time.minutes === reference.minutes;

/** Есть ли в строке запроса хотя бы один наш параметр */
export const hasUrlState = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  return ALL_KEYS.some(key => params.has(key));
};

/**
 * Читает состояние из query-параметров. В результате только те ключи, которые
 * реально присутствуют в URL (остальные — undefined, значение по умолчанию).
 */
export const readUrlState = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  const state = {};
  const value = (key) => {
    const raw = params.get(key);
    return isBlank(raw) ? undefined : raw;
  };

  const section = value(URL_KEYS.section);
  if (section) state.section = section;

  const tab = value(URL_KEYS.tab);
  if (tab) state.tab = tab;

  const startDate = parseDateParam(value(URL_KEYS.startDate), 'start');
  if (startDate) state.startDate = startDate;

  const endDate = parseDateParam(value(URL_KEYS.endDate), 'end');
  if (endDate) state.endDate = endDate;

  const startTime = parseTimeString(value(URL_KEYS.startTime));
  if (startTime) state.startTime = startTime;

  const endTime = parseTimeString(value(URL_KEYS.endTime));
  if (endTime) state.endTime = endTime;

  const project = value(URL_KEYS.project);
  if (project) state.project = project;

  const mediaSource = value(URL_KEYS.mediaSource);
  if (mediaSource) state.mediaSource = mediaSource;

  const platform = value(URL_KEYS.platform);
  if (platform) state.platform = platform;

  if (params.has(URL_KEYS.campaigns)) {
    state.campaigns = params.getAll(URL_KEYS.campaigns).filter(item => !isBlank(item));
  }

  if (params.has(URL_KEYS.clientVersions)) {
    state.clientVersions = params.getAll(URL_KEYS.clientVersions).filter(item => !isBlank(item));
  }

  if (params.has(URL_KEYS.installVersions)) {
    state.installVersions = params.getAll(URL_KEYS.installVersions).filter(item => !isBlank(item));
  }

  const fees = value(URL_KEYS.applyPlatformFees);
  if (fees !== undefined) state.applyPlatformFees = fees === '1' || fees === 'true';

  const organic = value(URL_KEYS.includeOrganic);
  if (organic !== undefined) state.includeOrganic = !(organic === '0' || organic === 'false');

  return state;
};

/** Собирает строку запроса; значения по умолчанию не пишем, чтобы ссылка была короткой */
export const buildSearch = (state = {}) => {
  const params = new URLSearchParams();

  if (state.section) params.set(URL_KEYS.section, state.section);
  if (state.tab) params.set(URL_KEYS.tab, state.tab);

  const startDate = formatDateParam(state.startDate);
  if (startDate) params.set(URL_KEYS.startDate, startDate);

  const endDate = formatDateParam(state.endDate);
  if (endDate) params.set(URL_KEYS.endDate, endDate);

  if (state.startTime && !sameTime(state.startTime, DEFAULT_START_TIME)) {
    params.set(URL_KEYS.startTime, formatTimeString(state.startTime.hours, state.startTime.minutes));
  }
  if (state.endTime && !sameTime(state.endTime, DEFAULT_END_TIME)) {
    params.set(URL_KEYS.endTime, formatTimeString(state.endTime.hours, state.endTime.minutes));
  }

  if (state.project && state.project !== 'all') params.set(URL_KEYS.project, state.project);
  if (state.mediaSource && state.mediaSource !== 'all') params.set(URL_KEYS.mediaSource, state.mediaSource);
  if (state.platform && state.platform !== 'all') params.set(URL_KEYS.platform, state.platform);

  (state.campaigns || []).forEach(campaign => {
    if (!isBlank(campaign)) params.append(URL_KEYS.campaigns, campaign);
  });

  (state.clientVersions || []).forEach(version => {
    if (!isBlank(version)) params.append(URL_KEYS.clientVersions, version);
  });

  (state.installVersions || []).forEach(version => {
    if (!isBlank(version)) params.append(URL_KEYS.installVersions, version);
  });

  if (state.applyPlatformFees) params.set(URL_KEYS.applyPlatformFees, '1');
  if (state.includeOrganic === false) params.set(URL_KEYS.includeOrganic, '0');

  return params.toString();
};

/**
 * Переписывает адресную строку под текущее состояние.
 * replaceState (не push): смена фильтра не должна плодить записи в истории,
 * ссылка в адресной строке всё равно всегда актуальна и готова к копированию.
 */
export const writeUrlState = (state) => {
  const search = buildSearch(state);
  const next = `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`;
  const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (next !== current) {
    window.history.replaceState(window.history.state, '', next);
  }
};
